import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";

function publicClient() {
  const url = process.env.SUPABASE_URL!;
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  return createClient<Database>(url, key, {
    auth: { persistSession: false, autoRefreshToken: false },
  });
}

const AnswersSchema = z.object({
  licensed: z.enum(["life-only", "life-health", "in-process", "not-yet"]),
  weekly_hours: z.enum(["5-10", "10-20", "20-40", "full-time"]),
  motivation: z
    .array(
      z.enum([
        "income",
        "helping-families",
        "flexibility",
        "career-change",
        "building-team",
      ]),
    )
    .min(1)
    .max(2),
  relationships: z.enum([
    "whole-point",
    "important",
    "fast-transactions",
    "unsure",
  ]),
  legacy_meaning: z.string().trim().min(10).max(800),
});

export type QuizAnswers = z.infer<typeof AnswersSchema>;

const InputSchema = z.object({
  session_id: z.string().min(8).max(64),
  email: z.string().trim().email().max(255).optional().or(z.literal("")),
  answers: AnswersSchema,
});

function computeScore(a: QuizAnswers): number {
  let s = 0;
  // Q1 license
  if (a.licensed === "life-only" || a.licensed === "life-health") s += 10;
  else if (a.licensed === "in-process") s += 5;
  // Q2 hours
  if (a.weekly_hours === "full-time") s += 25;
  else if (a.weekly_hours === "20-40") s += 18;
  else if (a.weekly_hours === "10-20") s += 8;
  // Q3 motivation
  if (a.motivation.includes("helping-families")) s += 15;
  if (a.motivation.includes("building-team")) s += 15;
  if (a.motivation.includes("income")) s += 5;
  // Q4 relationships
  if (a.relationships === "whole-point") s += 25;
  else if (a.relationships === "important") s += 12;
  // Q5 legacy
  if (a.legacy_meaning.length > 50) s += 10;
  if (/family|families|legacy|children|generation|heritage|kids/i.test(a.legacy_meaning)) s += 10;
  return Math.max(0, Math.min(100, s));
}

function pathFor(score: number): "strong-fit" | "possible-fit" | "not-a-fit" {
  if (score >= 80) return "strong-fit";
  if (score >= 50) return "possible-fit";
  return "not-a-fit";
}

export const submitQuizResponse = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => InputSchema.parse(d))
  .handler(async ({ data }) => {
    const score = computeScore(data.answers);
    const recommended_path = pathFor(score);
    const sb = publicClient();
    const { error } = await sb.from("advisor_quiz_responses").insert({
      session_id: data.session_id,
      email: data.email && data.email.length > 0 ? data.email : null,
      answers: data.answers,
      fit_score: score,
      recommended_path,
    });
    if (error) throw new Error(error.message);
    return { fit_score: score, recommended_path };
  });